import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getBillingSettings } from "../services/settingsApi";
import { canAccessPage } from "../utils/roleAccess";

function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function MenuItemIcon({ type }) {
  if (type === "profile") {
    return (
      <svg viewBox="0 0 24 24">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
      </svg>
    );
  }

  if (type === "team") {
    return (
      <svg viewBox="0 0 24 24">
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M16 3.13a4 4 0 0 1 0 7.75M23 21v-2a4 4 0 0 0-3-3.87M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
      </svg>
    );
  }

  if (type === "logout") {
    return (
      <svg viewBox="0 0 24 24">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24">
      <path d="M12 3v2.25M12 18.75V21M4.93 4.93l1.59 1.59M17.48 17.48l1.59 1.59M3 12h2.25M18.75 12H21M4.93 19.07l1.59-1.59M17.48 6.52l1.59-1.59M12 15.75A3.75 3.75 0 1 0 12 8.25a3.75 3.75 0 0 0 0 7.5z" />
    </svg>
  );
}

function formatNumber(value) {
  return new Intl.NumberFormat("en-US").format(Number(value || 0));
}

function formatCurrencyAed(value) {
  const parsed = Number(value || 0);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return "";
  }

  return `AED ${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: parsed % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(parsed)}`;
}

function titleCase(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export default function ProfileDropdown() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState(false);
  const rootRef = useRef(null);
  const billingRequestedRef = useRef(false);
  const canOpenSettings = canAccessPage(user, "settings");
  const canOpenTeam = canAccessPage(user, "team-management");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!open || !canOpenSettings || billingRequestedRef.current) {
      return undefined;
    }

    const controller = new AbortController();
    billingRequestedRef.current = true;
    setBillingLoading(true);
    setBillingError(false);

    getBillingSettings({ signal: controller.signal })
      .then((data) => {
        setBilling(data || null);
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          setBillingError(true);
        }
      })
      .finally(() => {
        setBillingLoading(false);
      });

    return () => controller.abort();
  }, [canOpenSettings, open]);

  if (!user) {
    return null;
  }

  const hasConfiguredPlan = Number(billing?.allowance_minutes || 0) > 0;
  const planName = hasConfiguredPlan ? billing?.plan_name || "Current plan" : "Plan not configured";
  const planStatus = hasConfiguredPlan ? billing?.billing_status || "active" : "setup needed";
  const monthlyCost = formatCurrencyAed(billing?.monthly_cost_aed);
  const includedMinutes = Number(billing?.allowance_minutes || 0);
  const activeUsers = Number(billing?.active_user_count || 0);
  const agentLimit = Number(billing?.agent_limit || 0);
  const languageLimit = billing?.language_limit ? titleCase(billing.language_limit) : "";

  const handleNavigate = (nextPath) => {
    setOpen(false);
    navigate(nextPath);
  };

  return (
    <div
      ref={rootRef}
      className={`profile-dropdown ${open ? "open" : ""}`.trim()}
    >
      <button
        type="button"
        className="profile-dropdown-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="profile-chip-avatar">{user.avatarInitials || "AA"}</span>
        <span className="profile-chip-copy">
          <span className="profile-chip-name">{user.name}</span>
          <span className="profile-chip-role">{user.role}</span>
        </span>
        <span className="profile-dropdown-chevron">
          <ChevronIcon />
        </span>
      </button>

      <div className="profile-dropdown-menu" role="menu" aria-label="Profile menu">
        <div className="profile-dropdown-heading">
          <div className="profile-dropdown-name">{user.name}</div>
          <div className="profile-dropdown-email">{user.email}</div>
        </div>
        {canOpenSettings ? (
          <div className="profile-subscription-card" aria-label="Current subscription">
            {billingLoading ? (
              <div className="profile-subscription-muted">Loading subscription...</div>
            ) : billingError ? (
              <div className="profile-subscription-muted">Subscription unavailable</div>
            ) : (
              <>
                <div className="profile-subscription-top">
                  <div>
                    <span className="profile-subscription-label">Current plan</span>
                    <strong>{planName}</strong>
                  </div>
                  <span className={`profile-subscription-status ${hasConfiguredPlan ? "is-active" : ""}`}>
                    {planStatus}
                  </span>
                </div>
                <div className="profile-subscription-meta">
                  <span>{hasConfiguredPlan ? `${formatNumber(includedMinutes)} mins` : "-- mins"}</span>
                  {monthlyCost ? <span>{monthlyCost}/mo</span> : null}
                  {agentLimit ? <span>{formatNumber(activeUsers)} / {formatNumber(agentLimit)} users</span> : null}
                  {languageLimit ? <span>{languageLimit} languages</span> : null}
                </div>
              </>
            )}
          </div>
        ) : null}
        {canOpenSettings ? (
          <button
            type="button"
            className="profile-dropdown-item"
            onClick={() => handleNavigate("/settings")}
          >
            <span className="profile-dropdown-item-icon">
              <MenuItemIcon type="settings" />
            </span>
            Settings
          </button>
        ) : null}
        {canOpenTeam ? (
          <button
            type="button"
            className="profile-dropdown-item"
            onClick={() => handleNavigate("/team-management")}
          >
            <span className="profile-dropdown-item-icon">
              <MenuItemIcon type="team" />
            </span>
            Team Management
          </button>
        ) : null}
        <button
          type="button"
          className="profile-dropdown-item danger"
          onClick={async () => {
            setOpen(false);
            await logout();
            navigate("/login", { replace: true });
          }}
        >
          <span className="profile-dropdown-item-icon">
            <MenuItemIcon type="logout" />
          </span>
          Logout
        </button>
      </div>
    </div>
  );
}
